import { removeCssZoomFromPixels } from './css-zoom-compensation'

import { Point } from '../../dictionary/geometry.types'

/**
 * A position in canvas-container pixels, which the host
 * uses to anchor floating menus and the config panel next to whatever was clicked.
 *
 * Everything here converts screen space into that space the same way — subtract the
 * `.react-flow` container origin, then divide out the CSS zoom the Command Center
 * applies above the canvas (see css-zoom-compensation).
 */
export type ViewportRect = Point & {
  width: number
  height: number
}

const EMPTY_RECT: ViewportRect = { x: 0, y: 0, width: 0, height: 0 }

/** Events arrive as React synthetic or native; only `target` and client coords matter. */
type CoordSource = { target: EventTarget | null }

/** The canvas container an event fired inside, or null when it fired elsewhere. */
export function canvasContainerRect({ target }: CoordSource): DOMRect | null {
  return (target as HTMLElement | null)?.closest?.('.react-flow')?.getBoundingClientRect() ?? null
}

/** The clicked node's own rect — anchors the config panel beside the node. */
export function nodeViewportRect(event: CoordSource, cssZoom: number): ViewportRect {
  const nodeElement = (event.target as HTMLElement | null)?.closest?.('.react-flow__node')
  const container = canvasContainerRect(event)
  if (!nodeElement || !container) {
    return EMPTY_RECT
  }
  const node = nodeElement.getBoundingClientRect()

  return projectedViewportRect(
    container,
    { x: node.left, y: node.top },
    { x: node.right, y: node.bottom },
    cssZoom
  )
}

/**
 * The cursor position itself. Falls back to raw client coords when the event did
 * not originate inside the canvas container.
 */
export function pointerViewportPoint(
  event: CoordSource & { clientX: number; clientY: number },
  cssZoom: number
): Point {
  const container = canvasContainerRect(event)

  return {
    x: removeCssZoomFromPixels(event.clientX - (container?.left ?? 0), cssZoom),
    y: removeCssZoomFromPixels(event.clientY - (container?.top ?? 0), cssZoom),
  }
}

/**
 * A rect already projected to screen space (React Flow's `flowToScreenPosition`),
 * expressed relative to the container — used for nav areas and the multiselection
 * bounding box, whose menus hang off the rect rather than off a click point.
 */
export function projectedViewportRect(
  container: DOMRect | null,
  topLeft: Point,
  bottomRight: Point,
  cssZoom: number
): ViewportRect {
  return {
    x: removeCssZoomFromPixels(topLeft.x - (container?.left ?? 0), cssZoom),
    y: removeCssZoomFromPixels(topLeft.y - (container?.top ?? 0), cssZoom),
    width: removeCssZoomFromPixels(bottomRight.x - topLeft.x, cssZoom),
    height: removeCssZoomFromPixels(bottomRight.y - topLeft.y, cssZoom),
  }
}
